import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserRound, MapPin } from "lucide-react";
import { api, money } from "@/lib/api";
import { formatPhone } from "@/lib/phone";
import { Birthdays } from "@/pages/Birthdays";
import { EmptyBranchNote } from "@/components/EmptyBranchNote";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 100;

export default function Customers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState("all");

  const reqId = useRef(0);

  const fetchPage = useCallback(
    (offset, replace) => {
      const myId = ++reqId.current;
      const setBusy = replace ? setLoading : setLoadingMore;
      setBusy(true);
      return api
        .get("/customers", {
          params: { search: search.trim() || undefined, offset, limit: PAGE_SIZE },
        })
        .then(({ data }) => {
          if (myId !== reqId.current) return; // a newer search/show-more superseded this
          setRows((prev) => (replace ? data.items : [...prev, ...data.items]));
          setTotal(data.total);
          setHasMore(data.has_more);
        })
        .catch(() => {
          if (myId === reqId.current && replace) {
            setRows([]);
            setTotal(0);
            setHasMore(false);
          }
        })
        .finally(() => {
          if (myId === reqId.current) setBusy(false);
        });
    },
    [search]
  );

  useEffect(() => {
    if (tab !== "all") return undefined;
    const t = setTimeout(() => fetchPage(0, true), 250);
    return () => clearTimeout(t);
  }, [fetchPage, tab]);

  return (
    <div className="fade-up" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold sm:text-4xl">Customers</h1>
        <Button
          variant="outline"
          className="h-10 rounded-none"
          onClick={() => navigate("/reports/suburbs")}
          data-testid="customers-by-suburb-link"
        >
          <MapPin className="mr-2 h-4 w-4" /> By suburb
        </Button>
      </div>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">
        One account per phone number — every invoice, deposit and payment in one place.
      </p>

      <div className="mt-6 flex w-fit gap-px border border-border bg-border">
        {[
          ["all", "All customers"],
          ["birthdays", "Birthdays"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`customers-tab-${key}`}
            className={`px-4 py-2 text-sm transition-colors duration-150 ${
              tab === key
                ? "bg-[#0A0A0A] font-semibold text-white"
                : "bg-white hover:bg-[#FBF8F2]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "birthdays" && <Birthdays />}

      {tab === "all" && (
      <>
      <div className="relative mt-7 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-10 rounded-none pl-9"
          placeholder="Search by phone number or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="customer-search-input"
        />
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div
          className="mt-10 border border-dashed border-border py-16 text-center"
          data-testid="customers-empty-state"
        >
          <UserRound className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            No customers found. They are created automatically with the first invoice.
          </p>
          {!search && <EmptyBranchNote className="mx-auto mt-5 max-w-xl" />}
        </div>
      ) : (
        <>
        <p className="mt-5 text-sm text-muted-foreground" data-testid="customers-count">
          Showing {rows.length} of {total} {total === 1 ? "customer" : "customers"}
          {search ? " matching your search" : ""}
        </p>
        <div className="mt-3 overflow-x-auto border border-border">
          <table className="w-full min-w-[760px] text-sm" data-testid="customers-table">
            <thead className="bg-[#F8F9FA] text-left">
              <tr className="border-b border-border">
                <th className="px-3 py-2.5 font-semibold">Customer</th>
                <th className="px-3 py-2.5 font-semibold">Phone</th>
                <th className="px-3 py-2.5 text-right font-semibold">Records</th>
                <th className="px-3 py-2.5 text-right font-semibold">Invoiced</th>
                <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((c, i) => (
                <tr
                  key={c.phone}
                  onClick={() => navigate(`/customers/${c.phone}`)}
                  className="cursor-pointer hover:bg-[#FBF8F2]"
                  data-testid={`customer-row-${i}`}
                >
                  <td className="px-3 py-2.5">
                    <div className="font-semibold">{c.name || "—"}</div>
                    {c.former_names?.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        formerly {c.former_names.join(", ")}
                      </div>
                    )}
                    {c.documents === 0 && c.source && (
                      <div
                        className="text-[11px] uppercase tracking-wider text-[#8A6C36]"
                        data-testid={`customer-source-${i}`}
                      >
                        {c.source === "queue" ? "from the queue" : "registered themselves"}
                      </div>
                    )}
                    {c.pending_update && (
                      <div
                        className="mt-1 inline-block bg-[#9A3412] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                        data-testid={`customer-pending-${i}`}
                      >
                        Details to review
                      </div>
                    )}
                  </td>
                  <td className="tabular px-3 py-2.5">{formatPhone(c.phone)}</td>
                  <td className="px-3 py-2.5 text-right">{c.documents}</td>
                  <td className="tabular px-3 py-2.5 text-right">{money(c.invoiced)}</td>
                  <td className="tabular px-3 py-2.5 text-right">{money(c.paid)}</td>
                  <td className="tabular px-3 py-2.5 text-right font-semibold">
                    {c.outstanding >= 0 ? (
                      money(c.outstanding)
                    ) : (
                      <span className="text-[#166534]">{money(-c.outstanding)} cr</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="mt-5 flex justify-center">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => fetchPage(rows.length, false)}
              disabled={loadingMore}
              data-testid="customers-show-more"
            >
              {loadingMore ? "Loading…" : `Show more (${total - rows.length} more)`}
            </Button>
          </div>
        )}
        </>
      )}
      </>
      )}
    </div>
  );
}
